import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Card,
  CardContent,
} from "@arcadeai/design-system/components/ui/atoms/card";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { Globe, Users } from "lucide-react";
import { type ReactNode, Suspense } from "react";
import { useOrgPermissions } from "@/app/capabilities/api/hooks";
import { ORG } from "@/app/capabilities/permissions";
import { useGetOrgMembers } from "@/app/org-members/api/hooks";
import { OrgMembersTable } from "@/app/org-members/components/org-members-table";
import { OrgMembersCardSkeleton } from "@/app/organizations/components/organization-page-skeleton";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { useDashboardOrgProject } from "@/lib/org-project-context/hooks";

function formatDate(value: string): string {
  if (!value) {
    return "Unknown";
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "Unknown"
    : parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

function MemberCountValue() {
  const { data } = useGetOrgMembers();
  return <>{data?.count ?? data?.items.length ?? 0}</>;
}

function OrganizationStat({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="mt-1 font-medium text-sm">{value}</dd>
    </div>
  );
}

function OrgMembersCard({ orgName }: { orgName: string }) {
  const { data } = useGetOrgMembers();
  const members = data?.items ?? [];
  const count = data?.count ?? members.length;

  return (
    <section className="rounded-xl border bg-card/50 p-4">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-tertiary/10 text-tertiary dark:bg-indigo-400/10 dark:text-indigo-400">
          <Users className="h-4 w-4" />
        </div>
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-base">
            Members
            <Badge variant="secondary">{count}</Badge>
          </h2>
          <p className="text-muted-foreground text-sm">People in {orgName}</p>
        </div>
      </div>

      {members.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-8 text-muted-foreground text-sm">
            <div className="mb-1 font-medium text-foreground">
              No members found
            </div>
            <div>People join the organization through its projects.</div>
          </CardContent>
        </Card>
      ) : (
        <OrgMembersTable members={members} />
      )}
    </section>
  );
}

export function OrganizationOverviewPage() {
  const { activeOrganization, projects } = useDashboardOrgProject();
  const { canOrg } = useOrgPermissions();
  const canReadMembers = canOrg(ORG.members.read);
  const canReadOrganization = canOrg(ORG.orgMetadata.read);

  const name = activeOrganization?.name ?? "Organization";
  const description = activeOrganization?.description?.trim();
  const createdAt = activeOrganization?.createdAt ?? "";
  const organizationId = activeOrganization?.id ?? "";
  const isDefault = activeOrganization?.isDefault ?? false;

  return (
    <ListLayout
      description="A summary of your organization and the people in it."
      title="Organization"
    >
      {canReadOrganization && (
        <section className="rounded-xl border bg-card/50">
          <div className="flex flex-col gap-5 p-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-brand-text">
                <Globe className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate font-semibold text-base leading-6">
                    {name}
                  </h2>
                  {isDefault && <Badge variant="secondary">Default</Badge>}
                </div>
                <p className="mt-1 max-w-3xl text-muted-foreground text-sm">
                  {description || "No description set."}
                </p>
              </div>
            </div>

            <dl className="grid min-w-0 grid-cols-3 gap-x-4 gap-y-3 sm:gap-x-8 lg:min-w-[24rem]">
              <OrganizationStat
                label="Projects"
                value={<span className="tabular-nums">{projects.length}</span>}
              />
              {canReadMembers && (
                <OrganizationStat
                  label="Members"
                  value={
                    <Suspense
                      fallback={
                        <Skeleton className="inline-block h-3.5 w-5 align-middle" />
                      }
                    >
                      <span className="tabular-nums">
                        <MemberCountValue />
                      </span>
                    </Suspense>
                  }
                />
              )}
              <OrganizationStat label="Created" value={formatDate(createdAt)} />
            </dl>
          </div>

          <div className="border-t px-4 py-3">
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
              <span className="shrink-0 text-muted-foreground text-xs">
                Organization ID
              </span>
              <div className="flex w-full min-w-0 items-center gap-1.5 sm:w-auto">
                <code className="block min-w-0 max-w-[calc(100vw-8rem)] overflow-x-auto whitespace-nowrap rounded-md bg-muted/60 px-2 py-1 font-mono text-muted-foreground text-xs leading-5 sm:max-w-md lg:max-w-lg">
                  {organizationId || "Unknown"}
                </code>
                <CopyButton
                  className="shrink-0"
                  content={organizationId}
                  disabled={!organizationId}
                  variant="ghost"
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {canReadMembers && (
        <Suspense fallback={<OrgMembersCardSkeleton />}>
          <OrgMembersCard
            orgName={canReadOrganization ? name : "this organization"}
          />
        </Suspense>
      )}
    </ListLayout>
  );
}
